import { Badge, Group, Text, UnstyledButton } from '@mantine/core';
import { clockTime, eventEmoji, formatOffset, severityBadgeVariant, type TimelineEventView } from './timelineEvent';

export interface TimelineEventRowProps<V extends TimelineEventView = TimelineEventView> {
  event: V;
  /** The group's earliest event — the offset ("+15s") next to the clock time is relative to this. */
  groupStart: Date;
  onClick?: (event: V) => void;
  /** Replaces the title, e.g. the floor of one alarm zone's member row; the place chip is then left out. */
  label?: string | null;
}

/** Compact single-line member row under an expanded Timeline group: absolute
 *  clock time with seconds + offset from the group's first event (so a burst's
 *  order and pacing reads at a glance), emoji, title, severity; actor and
 *  place from `sm` up. */
export function TimelineEventRow<V extends TimelineEventView = TimelineEventView>({
  event,
  groupStart,
  onClick,
  label,
}: TimelineEventRowProps<V>) {
  const where = label ? null : event.deviceName || event.location;
  return (
    <UnstyledButton
      onClick={() => onClick?.(event)}
      data-event-id={event.id}
      w="100%"
      px={{ base: 8, sm: 12 }}
      py={6}
      style={{ borderRadius: 'var(--mantine-radius-sm)', transition: 'background-color 150ms' }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
    >
      <Group gap={8} wrap="nowrap" fz="sm">
        <Text component="span" fz="xs" c="dimmed" style={{ fontFamily: 'monospace', flexShrink: 0 }}>
          {clockTime(event.time)}
        </Text>
        <Text component="span" fz="xs" c="dimmed" style={{ flexShrink: 0 }}>
          {formatOffset(event.time, groupStart)}
        </Text>
        <Text component="span" fz="sm" style={{ flexShrink: 0 }}>{eventEmoji(event.kind, event.title)}</Text>
        <Text component="span" fz="sm" fw={500} truncate miw={0} style={{ flex: '1 1 auto' }} title={event.title}>{label || event.title}</Text>
        <Badge variant={severityBadgeVariant(event.severity)} size="xs" style={{ flexShrink: 0 }}>
          {event.severity}
        </Badge>
        {event.actorName && (
          <Text component="span" fz="xs" c="dimmed" truncate maw={180} visibleFrom="sm" style={{ flexShrink: 1 }}>
            👤 {event.actorName}
          </Text>
        )}
        {where && (
          <Text component="span" fz="xs" c="dimmed" truncate maw={240} visibleFrom="sm" style={{ flexShrink: 1 }}>
            📍 {where}
          </Text>
        )}
      </Group>
    </UnstyledButton>
  );
}
